/**
 * Over-the-air (OTA) bundle updates for the Capacitor shell.
 *
 * The shell ships a baked-in copy of the Vite build, then on every cold start
 * it checks our manifest URL for a newer bundle. If newer, it downloads the
 * zip, verifies the SHA-256, and stages it as the next active bundle. The
 * **next** cold start loads from the staged path (so we never swap basepath
 * mid-session, which would leave React state stranded).
 *
 * We use `@capgo/capacitor-updater` for the native side because:
 *   - it handles the cold-start swap in Swift/Kotlin (no JS-bundle flash)
 *   - it includes a built-in rollback if the new bundle crashes on first boot
 *   - it's MIT-licensed and works against a self-hosted manifest URL (no SaaS)
 *
 * Apple guideline 4.7 explicitly allows JS bundle updates as long as they
 * don't change the app's "primary purpose". Native-shell changes
 * (new Capacitor plugins, new native permissions) still go through review.
 * The manifest's `minNativeVersion` gate enforces that — clients on an older
 * shell ignore bundles that demand a newer one.
 */

import { isNative } from '../runtime/index.ts';

export interface BundleManifest {
  /** Semver of the JS bundle, e.g. "2026.05.15-1". */
  version: string;
  /** Public download URL of the zipped Vite build. */
  url: string;
  /** Hex-encoded SHA-256 of the zip — verified before activation. */
  sha256: string;
  /** Minimum native-shell version required to load this bundle. */
  minNativeVersion: string;
  /** ISO timestamp — informational, not load-bearing. */
  releasedAt: string;
  /** Optional human-readable note shown in admin tooling. */
  notes?: string;
}

export interface CheckResult {
  applied: boolean;
  /** Version of the staged bundle (if applied) or current bundle (if no-op). */
  version: string;
  reason?: string;
}

export interface UpdateAvailability {
  available: boolean;
  /** Currently-active bundle version (e.g. 'builtin' or a date-sha string). */
  currentVersion: string;
  /** Available new version, if any. Falsy when `available` is false. */
  newVersion?: string;
  /** Hex-encoded SHA-256 of the new bundle's zip. */
  sha256?: string;
  /** Public download URL of the new bundle. */
  url?: string;
}

export interface DownloadProgressEvent {
  /** 0–100 inclusive. The plugin batches updates roughly every 64KB. */
  percent: number;
}

export type DownloadProgressListener = (event: DownloadProgressEvent) => void;

/**
 * Fetch the manifest, decide whether to update, and stage the new bundle if so.
 * Safe to call on web — returns `{ applied: false, version: 'web' }`.
 *
 * Call this once at app boot (after the user is authenticated isn't required —
 * earlier is better, so the next launch already has the latest UI). Network
 * failures are swallowed; we'd rather keep using the old bundle than spin.
 */
export async function checkAndStageBundle(manifestUrl: string): Promise<CheckResult> {
  if (!isNative()) return { applied: false, version: 'web', reason: 'not-native' };

  try {
    const { CapacitorUpdater } = await import('@capgo/capacitor-updater');

    // Notify the plugin that the previous update (if any) is healthy. Without
    // this, the plugin rolls back to the previous bundle on next launch.
    await CapacitorUpdater.notifyAppReady().catch(() => {});

    const current = await CapacitorUpdater.current();
    const currentVersion = current.bundle?.version ?? 'builtin';

    const res = await fetch(manifestUrl, { cache: 'no-store' });
    if (!res.ok) {
      return { applied: false, version: currentVersion, reason: `manifest-${res.status}` };
    }
    const manifest = (await res.json()) as BundleManifest;

    if (!manifest.version || manifest.version === currentVersion) {
      return { applied: false, version: currentVersion, reason: 'up-to-date' };
    }

    // The plugin will fetch + extract + checksum on its own.
    const installed = await CapacitorUpdater.download({
      version: manifest.version,
      url: manifest.url,
      checksum: manifest.sha256,
    });

    // Stage as next launch — we do NOT call `.set()` here because that
    // reloads the webview immediately, which would kill in-flight React state
    // and feels jarring. The next cold start will pick it up.
    await CapacitorUpdater.next({ id: installed.id });

    return { applied: true, version: manifest.version };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return { applied: false, version: 'unknown', reason: msg };
  }
}

/**
 * Tell the OTA plugin "this bundle booted successfully" — required, otherwise
 * the plugin rolls back to the previous bundle on the next launch. Call once,
 * after the initial render has completed without crashing.
 *
 * Resilient version: on Android, Capgo's WebView reload races Capacitor's
 * bridge injection. If we call notifyAppReady before the bridge is ready,
 * the call routes through the JS-side web shim (which is a no-op) instead
 * of the native plugin — Capgo never gets the signal, marks the bundle as
 * failed after 30s, and rolls back. To survive this:
 *
 *   1. Poll for the Capacitor bridge (window.Capacitor.getPlatform()
 *      returning 'ios' / 'android') for up to 5s.
 *   2. Once the bridge is ready, call notifyAppReady. Retry up to 5 times
 *      with backoff in case the plugin proxy is still routing to the web
 *      shim during the first beats after bridge initialisation.
 *   3. Log every attempt to console (forwarded to logcat / Xcode console)
 *      so we can confirm from native logs that the call actually fired.
 */
export async function markBundleHealthy(): Promise<void> {
  // Poll for window.Capacitor.Plugins.CapacitorUpdater to exist, then
  // fire notifyAppReady once via the native bridge.
  //
  // History: earlier versions probed the bridge with a current() round-
  // trip and spammed notifyAppReady 10× over 3s to defeat a race where
  // the new bundle's JS ran before Capgo's plugin registry was fully
  // wired post-reload. The actual root cause turned out to be the
  // JS-initiated set() path (UpdateGate auto-apply) wedging the bridge —
  // now that downloadAndApplyUpdate uses next() on Android, the new
  // bundle is always loaded via the stub's reliable cold-start path
  // where the bridge is wired before any JS runs. A single call lands
  // every time, so the probe + spam workaround is no longer needed.
  type CapacitorBridge = {
    getPlatform?: () => string;
    Plugins?: { CapacitorUpdater?: { notifyAppReady?: () => unknown } };
  };
  for (let i = 0; i < 100; i += 1) {
    const cap = (globalThis as { Capacitor?: CapacitorBridge }).Capacitor;
    const platform = cap?.getPlatform?.();
    const updater = cap?.Plugins?.CapacitorUpdater;
    if ((platform === 'ios' || platform === 'android') && updater?.notifyAppReady) {
      try {
        const ret = updater.notifyAppReady();
        if (ret && typeof (ret as Promise<unknown>).then === 'function') {
          (ret as Promise<unknown>).catch((err) => {
            console.warn('[markBundleHealthy] post-call rejected:', err);
          });
        }
        return;
      } catch (err) {
        console.error('[markBundleHealthy] threw:', err);
        return;
      }
    }
    if (i === 99) {
      console.log('[markBundleHealthy] plugin never registered after 5s — skipping');
      return;
    }
    await new Promise((r) => setTimeout(r, 50));
  }
}

/**
 * Manifest-only check: returns whether an update is available without
 * downloading anything. Used by the foreground listener — we don't want to
 * burn a megabyte of cellular every time the user switches apps.
 *
 * `currentVersion` is passed in by the caller because the source of truth
 * for "what JS bundle am I running" is the bundle itself (via a build-time
 * env stamp like VITE_BUILD_VERSION), not Capgo's
 * `current().bundle.version` — Capgo reports `"1.0"` for the built-in
 * bundle on a fresh install, which would falsely flag every published
 * release as an update.
 *
 * Returns `available: false` on web, on network failure, and when already
 * up-to-date. The caller decides what to do with the result (banner vs.
 * automatic download).
 */
export async function checkForUpdate(
  manifestUrl: string,
  currentVersion: string,
): Promise<UpdateAvailability> {
  if (!isNative()) {
    return { available: false, currentVersion };
  }
  try {
    const res = await fetch(manifestUrl, { cache: 'no-store' });
    if (!res.ok) return { available: false, currentVersion };

    const manifest = (await res.json()) as BundleManifest;
    if (!manifest.version || manifest.version === currentVersion) {
      return { available: false, currentVersion };
    }
    return {
      available: true,
      currentVersion,
      newVersion: manifest.version,
      sha256: manifest.sha256,
      url: manifest.url,
    };
  } catch {
    // Manifest unreachable / malformed — pretend nothing's available rather
    // than letting the UI spin on a broken release.
    return { available: false, currentVersion };
  }
}

/**
 * Download the bundle described by `availability` and apply it in place.
 *
 * Pattern: download → next → reload. We deliberately do not use
 * CapacitorUpdater.set() — set()'s internal _reload() helper runs on the
 * Capacitor plugin HandlerThread and blocks waiting for notifyAppReady(),
 * which is itself queued behind it on the same thread. Deadlock; Capgo's
 * appReadyTimeout fires and rolls back. The dedicated reload() PluginMethod
 * is wrapped in startNewThread (Cap-go PR #754, fix landed in
 * @capgo/capacitor-updater 8.45.10+), avoiding the deadlock. Equivalent
 * outcome to set() but on a thread Capgo won't deadlock itself on.
 *
 * iOS doesn't have the deadlock (different threading model) but uses the
 * same path for symmetry — reload() works fine there too.
 *
 * `onProgress` fires with the download percentage from the Capgo plugin's
 * native `download` event. Decompression and filesystem write happen after
 * 100% — total user-perceived time is ~1–2 seconds longer than the raw
 * download duration on good hardware.
 */
export async function downloadAndApplyUpdate(
  availability: UpdateAvailability,
  onProgress?: DownloadProgressListener,
): Promise<{ ok: true; appliedAt: 'now' | 'next-launch' } | { ok: false; reason: string }> {
  if (!isNative()) return { ok: false, reason: 'not-native' };
  if (!availability.available || !availability.newVersion || !availability.url || !availability.sha256) {
    return { ok: false, reason: 'no-update' };
  }

  const { CapacitorUpdater } = await import('@capgo/capacitor-updater');

  let progressHandle: { remove: () => Promise<void> } | null = null;
  if (onProgress) {
    progressHandle = await CapacitorUpdater.addListener('download', (event: { percent: number }) => {
      onProgress({ percent: event.percent });
    });
  }

  try {
    const installed = await CapacitorUpdater.download({
      version: availability.newVersion,
      url: availability.url,
      checksum: availability.sha256,
    });
    await CapacitorUpdater.next({ id: installed.id });
    // reload() returns once the WebView reload kicks off — the new bundle's
    // JS will run shortly after and call notifyAppReady() via markBundleHealthy.
    await CapacitorUpdater.reload();
    return { ok: true, appliedAt: 'now' };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    return { ok: false, reason: msg };
  } finally {
    await progressHandle?.remove().catch(() => {});
  }
}
